import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Play, RefreshCw, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import TickerMarquee from "../components/TickerMarquee";
import { getMarketWindowForNow } from "../utils/marketStatus";
import { isAppWebView } from "../utils/appWebView";
import { openExternalLink } from "../utils/appInteractions";

// ─── Brand Colors (BharatMatka style) ───────────────────────────────────────
const ORANGE = "#f97316";
const GREEN  = "#16a34a";
const PURPLE = "#312e81";

// ─── Root Component ──────────────────────────────────────────────────────────
function Home() {
  const navigate = useNavigate();
  const [settings, setSettings]             = useState({});
  const [markets, setMarkets]               = useState([]);
  const [normalRateMap, setNormalRateMap]   = useState({});
  const [liveTime, setLiveTime]             = useState(() => fmtLiveTime(new Date()));
  const [refreshing, setRefreshing]         = useState(false);
  const [menuOpen, setMenuOpen]             = useState(false);
  const [vw, setVw]                         = useState(() => window.innerWidth);
  const [openFaq, setOpenFaq]               = useState(null);

  const isMobile  = vw < 768;
  const isTablet  = vw >= 768 && vw < 1024;
  const inWebView = isAppWebView();

  // ── helpers ─────────────────────────────────────────────────────────────────
  const downloadUrl   = useMemo(() => String(settings.app_download_url   || "").trim(), [settings.app_download_url]);
  const downloadLabel = useMemo(() => String(settings.app_download_label || "").trim() || "Download App", [settings.app_download_label]);
  const tickerText    = useMemo(() => String(settings.home_running_notice || "Auto Deposit | Auto Withdraw | Fast Results | Secure Wallet | Play Online Satta Matka").trim(), [settings.home_running_notice]);

  const displayMarkets = useMemo(() =>
    markets.map((item) => {
      const win       = getMarketWindowForNow(item);
      const openT     = win.open_time  || item.open_time;
      const closeT    = win.close_time || item.close_time;
      const nowM      = new Date().getHours() * 60 + new Date().getMinutes();
      const openM     = toMins(openT);
      const closeM    = toMins(closeT);
      const isOpen    = openM >= 0 && closeM > openM && nowM >= openM && nowM < closeM;
      return { id: item.id, name: item.name, result: item.result || "***-**-***", openTime: fmtTime(openT), closeTime: fmtTime(closeT), isOpen };
    }),
    [markets]
  );

  const displayRates = useMemo(() => RATE_META.map((m) => ({ ...m, val: Number(normalRateMap?.[m.key] || 0) })), [normalRateMap]);

  // ── data loading ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [mR, rR, sR] = await Promise.allSettled([
        API.get("/markets"),
        API.get("/game-rates?market_type=normal"),
        API.get("/settings/public"),
      ]);
      if (mR.status === "fulfilled") {
        setMarkets(
          (Array.isArray(mR.value.data) ? mR.value.data : [])
            .filter((i) => (i.market_type || "normal") !== "starline")
            .sort((a, b) => toMins(a.open_time) - toMins(b.open_time))
        );
      }
      if (rR.status === "fulfilled") setNormalRateMap(rR.value.data || {});
      if (sR.status === "fulfilled") setSettings(sR.value.data || {});
      setLiveTime(fmtLiveTime(new Date()));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setLiveTime(fmtLiveTime(new Date())), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const h = () => setVw(window.innerWidth); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);

  // ── actions ──────────────────────────────────────────────────────────────────
  const goPlay     = ()     => navigate("/login");
  const goCharts   = ()     => navigate("/charts");
  const goMktChart = (id, v)=> id ? navigate(`/charts/market/${id}?view=${v}`) : navigate("/charts");
  const goAbout    = ()     => navigate("/about-us");
  const doDownload = ()     => { if (downloadUrl) openExternalLink(downloadUrl); else alert("Download link coming soon"); };

  const navLinks = [
    { label: "Register", primary: true, action: goPlay    },
    { label: "Charts",                 action: goCharts  },
    { label: "About Us",               action: goAbout   },
  ];

  const marketList = displayMarkets.length ? displayMarkets : FALLBACK_MARKETS;

  // ── grid columns ─────────────────────────────────────────────────────────────
  const mktCols = isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)";

  return (
    <div style={pg}>

      {/* ── MOBILE DRAWER OVERLAY ─────────────────────────────────────────── */}
      {menuOpen && <div style={drwOverlay} onClick={() => setMenuOpen(false)} />}
      <aside style={{ ...drawer, transform: menuOpen ? "translateX(0)" : "translateX(-100%)" }}>
        <div style={drwHead}>
          <BrandText small />
          <button style={iconBtn} onClick={() => setMenuOpen(false)}><X size={20} /></button>
        </div>
        {navLinks.map((n) => (
          <button key={n.label} style={drwItem} onClick={() => { setMenuOpen(false); n.action(); }}>
            {n.label}
          </button>
        ))}
        {!inWebView && downloadUrl && (
          <button style={{ ...drwItem, color: GREEN, borderColor: GREEN }} onClick={() => { setMenuOpen(false); doDownload(); }}>
            {downloadLabel}
          </button>
        )}
      </aside>

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <header style={hdr}>
        <button style={brandBtn} onClick={goPlay}><BrandText /></button>

        {!isMobile && (
          <nav style={topNav}>
            {navLinks.map((n) => (
              <button
                key={n.label}
                style={n.primary ? navBtnPrimary : navBtn}
                onClick={n.action}
              >
                {n.label}
              </button>
            ))}
            {!inWebView && downloadUrl && (
              <button style={navBtnGreen} onClick={doDownload}>{downloadLabel}</button>
            )}
          </nav>
        )}

        {isMobile && (
          <button style={iconBtn} onClick={() => setMenuOpen(true)}><Menu size={26} color="#111827" /></button>
        )}
      </header>

      {/* ── NOTICE TICKER ────────────────────────────────────────────────────── */}
      <div style={ticker}>
        <TickerMarquee
          text={tickerText}
          speed={55}
          outerStyle={tickerOuter}
          trackStyle={tickerTrack}
          textStyle={tickerTxt}
        />
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section style={{ ...heroWrap, flexDirection: isMobile ? "column" : "row" }}>
        {/* Left */}
        <div style={heroLeft}>
          <p style={heroUrl}>www.playonlinesattamatka.com</p>
          <h1 style={heroH1}>
            <span style={{ color: ORANGE }}>Play Online</span>
            <br />
            <span style={{ color: GREEN }}>Satta Matka</span>
            <br />
            <span style={{ color: "#fff", fontSize: "clamp(13px,2.5vw,18px)", fontWeight: 700, opacity: 0.85 }}>Live Results, Charts &amp; Game Rates</span>
          </h1>
          <p style={heroTagline}>Best Rates • Fast Results • Secure Wallet</p>
          <div style={liveBadge}>🟢 Live — Updated {liveTime}</div>
          <div style={heroActions}>
            <button style={bigPlayBtn} onClick={goPlay}>
              <Play size={18} fill="#fff" color="#fff" />
              Play Now
            </button>
            {!inWebView && downloadUrl && (
              <button style={bigDlBtn} onClick={doDownload}>{downloadLabel}</button>
            )}
          </div>
          <p style={ageNote}>18+ Only • Play Responsibly</p>
        </div>

        {/* Right — Game Win Rates card */}
        <div style={{ ...heroRight, width: isMobile ? "100%" : 320 }}>
          <div style={rateCard}>
            <div style={rateCardHdr}>
              <span>🎮 Game Win Rates</span>
              <span style={rateCardBadge}>LIVE</span>
            </div>
            {displayRates.map((r) => (
              <div key={r.key} style={rateRow}>
                <span style={{ ...rateBadge, background: r.color }}>{r.badge}</span>
                <span style={rateName}>{r.title}</span>
                <span style={rateAmt}>{fmtRateRupee(r.val)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AVAILABLE GAMES / MARKETS ─────────────────────────────────────────── */}
      <section style={section}>
        <div style={secHead}>
          <div>
            <h2 style={secTitle}>Available Games</h2>
            <p style={secSub}>We have multiple types of games for you</p>
          </div>
          <button style={refreshBtnStyle} onClick={load} disabled={refreshing}>
            <RefreshCw size={14} />
            {refreshing ? "Updating..." : "Refresh"}
          </button>
        </div>

        <div style={{ ...mktGrid, gridTemplateColumns: mktCols }}>
          {marketList.map((m) => (
            <div key={m.id || m.name} style={mktCard}>
              {/* Top row: name + status */}
              <div style={mktTopRow}>
                <span style={mktName}>{m.name}</span>
                <span style={{ ...statusPill, background: m.isOpen ? "#dcfce7" : "#fee2e2", color: m.isOpen ? "#15803d" : "#dc2626" }}>
                  {m.isOpen ? "Open" : "Closed"}
                </span>
              </div>

              {/* Time */}
              <div style={mktTime}>{m.openTime} — {m.closeTime}</div>

              {/* Result */}
              <div style={mktResult}>{m.result}</div>

              {/* Chart links + Play Now */}
              <div style={mktFoot}>
                <div style={chartRow}>
                  <button style={chartLnk} onClick={(e) => { e.stopPropagation(); goMktChart(m.id, "jodi"); }}>
                    Jodi Chart
                  </button>
                  <span style={divider}>|</span>
                  <button style={chartLnk} onClick={(e) => { e.stopPropagation(); goMktChart(m.id, "panel"); }}>
                    Panna Chart
                  </button>
                </div>
                <button style={playNowBtn} onClick={goPlay}>
                  Play Now <Play size={13} fill="#fff" color="#fff" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── APP DOWNLOAD STRIP ────────────────────────────────────────────────── */}
      {!inWebView && downloadUrl && (
        <div style={dlStrip}>
          <img src="/Google_Play_2016_icon.svg" alt="" style={{ width: 28, height: 28 }} />
          <strong style={{ fontSize: 16, fontWeight: 900 }}>{downloadLabel}</strong>
          <button style={dlBtn} onClick={doDownload}>Download Now</button>
        </div>
      )}

      {/* ── WHY CHOOSE US ────────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="why-choose-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Trusted Platform</p>
          <h2 id="why-choose-heading" style={seoH2}>Why Choose Play Online Satta Matka?</h2>
          <p style={seoLead}>Lakhs of players across India trust us for the best matka rates, instant wallet, and live results every day.</p>
          <div style={{ ...featGrid, gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "1fr 1fr 1fr" : "repeat(3,1fr)" }}>
            {HOME_FEATURES.map((f) => (
              <article key={f.title} style={featCard}>
                <div style={featIcon}>{f.icon}</div>
                <h3 style={featTitle}>{f.title}</h3>
                <p style={featBody}>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT IS SATTA MATKA ───────────────────────────────────────────────── */}
      <section style={{ ...seoSection, background: "#fff7ed", padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="what-is-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Learn</p>
          <h2 id="what-is-heading" style={seoH2}>What is Online Satta Matka?</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: isMobile ? 20 : 32, alignItems: "start" }}>
            <div>
              <p style={seoBody}>
                <strong>Satta Matka</strong> is one of India's most popular number-based games of chance. Originally started in Mumbai, Matka has evolved into a fully digital experience where players can bet on numbers, view live results, track charts, and manage their wallet — all from their phone.
              </p>
              <p style={seoBody}>
                Every Matka market has an <strong>Open</strong> and <strong>Close</strong> session. Players choose numbers like Single Digit (0–9), Jodi (00–99), or Patti (a 3-digit combination). Results are declared at the market closing time and winners receive payouts based on the rates.
              </p>
              <p style={seoBody}>
                Popular markets include <strong>Kalyan Matka</strong>, <strong>Milan Day/Night</strong>, <strong>Rajdhani Day/Night</strong>, <strong>Main Bazar</strong>, and Starline markets. Each market has different timings and win-rate multipliers.
              </p>
            </div>
            <div>
              <div style={rateInfoBox}>
                <div style={rateInfoHead}>Game Win Rates</div>
                {[
                  ["Single Digit (0–9)", normalRateMap.single],
                  ["Jodi (00–99)",        normalRateMap.jodi],
                  ["Single Patti",        normalRateMap.sp],
                  ["Double Patti",        normalRateMap.dp],
                  ["Triple Patti",        normalRateMap.tp],
                  ["Half Sangam",         normalRateMap.half_sangam],
                  ["Full Sangam",         normalRateMap.full_sangam],
                ].map(([name, rate]) => (
                  <div key={name} style={rateInfoRow}>
                    <span style={rateInfoName}>{name}</span>
                    <span style={rateInfoVal}>{rate ? `${rate}× your bet` : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={seoCtaRow}>
            <button style={seoPrimaryBtn} onClick={() => navigate("/how-to-play")}>How to Play Guide →</button>
            <button style={seoSecondaryBtn} onClick={() => navigate("/charts")}>View Matka Charts →</button>
          </div>
        </div>
      </section>

      {/* ── HOW TO PLAY STEPS ────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="how-to-play-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Quick Start</p>
          <h2 id="how-to-play-heading" style={seoH2}>How to Play Matka Online — 4 Easy Steps</h2>
          <div style={{ ...stepsGrid, gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)" }}>
            {HOW_STEPS.map((s, i) => (
              <div key={s.title} style={stepCard}>
                <div style={stepNum}>{i + 1}</div>
                <h3 style={stepTitle}>{s.title}</h3>
                <p style={stepDesc}>{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={seoCtaRow}>
            <button style={seoPrimaryBtn} onClick={() => navigate("/login")}>Register Free Now →</button>
            <button style={seoSecondaryBtn} onClick={() => navigate("/game-rates")}>See All Game Rates →</button>
          </div>
        </div>
      </section>

      {/* ── CUSTOMER REVIEWS ──────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, background: "#f0fdf4", padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="reviews-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Reviews & Rating</p>
          <h2 id="reviews-heading" style={seoH2}>What Our Players Say</h2>
          <div style={ratingBig}>
            <span style={ratingScore}>4.8</span>
            <div>
              <div style={starsRow}>{"★★★★★"}</div>
              <p style={ratingNote}>Based on 12,000+ player reviews</p>
            </div>
          </div>
          <div style={{ ...reviewGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)" }}>
            {HOME_REVIEWS.map((r) => (
              <article key={r.name} style={reviewCard}>
                <div style={reviewTop}>
                  <div style={{ ...reviewAvatar, background: r.color }}>{r.name[0]}</div>
                  <div>
                    <strong style={reviewName}>{r.name}</strong>
                    <div style={reviewStars}>{"★".repeat(r.stars)}</div>
                  </div>
                  <span style={verifiedBadge}>✓ Verified</span>
                </div>
                <p style={reviewText}>{r.text}</p>
                <span style={reviewDate}>{r.date}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="faq-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Help & Support</p>
          <h2 id="faq-heading" style={seoH2}>Frequently Asked Questions</h2>
          <div style={faqList}>
            {HOME_FAQS.map((faq, i) => (
              <div key={faq.q} style={faqItem}>
                <button
                  type="button"
                  style={faqQuestion}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <span style={faqChevron}>{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && <p style={faqAnswer}>{faq.a}</p>}
              </div>
            ))}
          </div>
          <div style={seoCtaRow}>
            <button style={seoSecondaryBtn} onClick={() => navigate("/faq")}>View All FAQs →</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer style={ftr}>
        <div style={{ marginBottom: 14 }}><BrandText light /></div>
        <p style={ftrNote}>Players must be 18 years or above to register. Play Responsibly. Underage gaming is prohibited.</p>
        <div style={ftrLinks}>
          {[
            ["Home",                "/"],
            ["How to Play",         "/how-to-play"],
            ["Matka Charts",        "/charts"],
            ["Blog",                "/blog"],
            ["FAQ",                 "/faq"],
            ["About Us",            "/about-us"],
            ["Privacy Policy",      "/privacy-policy"],
            ["Terms & Conditions",  "/terms-and-conditions"],
            ["Refund Policy",       "/refund-policy"],
            ["Responsible Gaming",  "/responsible-gaming"],
            ["Contact Us",          "/contact-us"],
          ].map(([lbl, path]) => (
            <button key={path} style={ftrLnk} onClick={() => navigate(path)}>{lbl}</button>
          ))}
        </div>
        <div style={ftrDivider} />
        <p style={ftrCopy}>© 2026 Play Online Satta Matka. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

// ─── Brand Text ──────────────────────────────────────────────────────────────
function BrandText({ small = false, light = false }) {
  const base = { fontFamily: "'Arial Black', Impact, sans-serif", fontWeight: 900, lineHeight: 1, display: "inline-flex", alignItems: "center", gap: 0 };
  const sz   = small ? 20 : 24;
  return (
    <span style={{ ...base, fontSize: sz }}>
      <span style={{ color: light ? "#fbbf24" : ORANGE }}>Play Online&nbsp;</span>
      <span style={{ color: light ? "#86efac" : GREEN  }}>Satta Matka</span>
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtTime(v) {
  if (!v) return "--:--";
  const [h, m = "00"] = String(v).split(":");
  const hr = Number(h);
  return `${String(((hr + 11) % 12) + 1).padStart(2, "0")}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}

function fmtLiveTime(d) {
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function toMins(v) {
  const m = String(v || "").match(/(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : -1;
}

function fmtRateRupee(val) {
  const n = Number(val || 0);
  if (!n) return "—";
  const p = n * 10;
  return `₹${Number.isInteger(p) ? p : p.toFixed(1)}`;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const RATE_META = [
  { key: "single",       title: "Single Digit",  badge: "SD", color: "#ec4899" },
  { key: "jodi",         title: "Jodi Digit",    badge: "JD", color: "#3b82f6" },
  { key: "red_bracket",  title: "Red Bracket",   badge: "RB", color: "#f97316" },
  { key: "sp",           title: "Single Pana",   badge: "SP", color: "#22c55e" },
  { key: "dp",           title: "Double Pana",   badge: "DP", color: "#a855f7" },
  { key: "tp",           title: "Triple Pana",   badge: "TP", color: "#1d4ed8" },
  { key: "half_sangam",  title: "Half Sangam",   badge: "HS", color: "#0d9488" },
  { key: "full_sangam",  title: "Full Sangam",   badge: "FS", color: "#7c3aed" },
];

const FALLBACK_MARKETS = [
  { id: 1, name: "MILAN MORNING",  result: "***-**-***", openTime: "10:10 AM", closeTime: "11:11 AM", isOpen: false },
  { id: 2, name: "SRIDEVI",        result: "***-**-***", openTime: "11:25 AM", closeTime: "12:25 PM", isOpen: false },
  { id: 3, name: "TIME BAZAR",     result: "***-**-***", openTime: "12:45 PM", closeTime: "01:45 PM", isOpen: false },
  { id: 4, name: "RAJDHANI DAY",   result: "***-**-***", openTime: "03:25 PM", closeTime: "05:25 PM", isOpen: false },
  { id: 5, name: "KALYAN",         result: "***-**-***", openTime: "03:45 PM", closeTime: "05:45 PM", isOpen: false },
  { id: 6, name: "MAIN BAZAR",     result: "***-**-***", openTime: "09:30 PM", closeTime: "12:02 AM", isOpen: false },
];

// ─── Styles ──────────────────────────────────────────────────────────────────
const pg          = { minHeight: "100vh", background: "#f9fafb", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" };

// overlay + drawer
const drwOverlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 90 };
const drawer      = { position: "fixed", top: 0, left: 0, bottom: 0, width: 270, background: "#fff", zIndex: 100, padding: "14px 14px 24px", display: "flex", flexDirection: "column", gap: 10, boxShadow: "4px 0 24px rgba(0,0,0,.14)", transition: "transform .28s ease", overflowY: "auto" };
const drwHead     = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 };
const drwItem     = { padding: "13px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, background: "#fff", textAlign: "left", fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#111827" };

// header
const hdr         = { position: "sticky", top: 0, zIndex: 80, background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 66, boxShadow: "0 2px 8px rgba(0,0,0,.06)" };
const brandBtn    = { border: 0, background: "transparent", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" };
const iconBtn     = { border: 0, background: "#f3f4f6", borderRadius: 9, padding: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" };
const topNav      = { display: "flex", alignItems: "center", gap: 8 };
const navBtn      = { padding: "8px 16px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#111827", fontWeight: 700, fontSize: 14, cursor: "pointer" };
const navBtnPrimary = { ...navBtn, background: ORANGE, borderColor: ORANGE, color: "#fff" };
const navBtnGreen   = { ...navBtn, background: GREEN,  borderColor: GREEN,  color: "#fff" };

// ticker
const ticker      = { background: "#fff7ed", borderBottom: "1px solid #fed7aa" };
const tickerOuter = { width: "100%", minHeight: 36, overflow: "hidden" };
const tickerTrack = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform" };
const tickerTxt   = { display: "inline-flex", alignItems: "center", minHeight: 36, padding: "0 30px", color: "#c2410c", fontSize: 13, fontWeight: 700, letterSpacing: "0.02em" };

// hero
const heroWrap    = { display: "flex", alignItems: "flex-start", gap: 28, padding: "36px 24px 32px", maxWidth: 1200, margin: "0 auto", flexWrap: "wrap", background: "#fff", borderBottom: "1px solid #e5e7eb" };
const heroLeft    = { flex: "1 1 300px", minWidth: 0 };
const heroRight   = { flex: "0 1 300px" };
const heroUrl     = { margin: "0 0 6px", fontSize: 13, color: "#6b7280", fontWeight: 600 };
const heroH1      = { margin: "0 0 10px", fontSize: "clamp(28px,5vw,46px)", fontWeight: 900, lineHeight: 1.1 };
const heroTagline = { margin: "0 0 12px", fontSize: 15, color: "#374151" };
const liveBadge   = { display: "inline-flex", padding: "5px 14px", borderRadius: 999, background: "#f0fdf4", color: "#15803d", fontSize: 13, fontWeight: 700, border: "1px solid #bbf7d0", marginBottom: 18 };
const heroActions = { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 };
const bigPlayBtn  = { height: 48, padding: "0 26px", borderRadius: 10, border: 0, background: ORANGE, color: "#fff", fontWeight: 800, fontSize: 16, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", boxShadow: "0 4px 14px rgba(249,115,22,.35)" };
const bigDlBtn    = { height: 48, padding: "0 22px", borderRadius: 10, border: "2px solid " + PURPLE, background: "#fff", color: PURPLE, fontWeight: 800, fontSize: 14, cursor: "pointer" };
const ageNote     = { margin: 0, fontSize: 12, color: "#9ca3af" };

// rates card (hero right)
const rateCard    = { border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff", boxShadow: "0 6px 20px rgba(0,0,0,.09)" };
const rateCardHdr = { background: PURPLE, color: "#fff", padding: "11px 16px", fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "space-between" };
const rateCardBadge = { fontSize: 11, fontWeight: 900, background: "#f97316", color: "#fff", padding: "2px 8px", borderRadius: 999 };
const rateRow     = { display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f3f4f6" };
const rateBadge   = { width: 30, height: 22, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 900, flexShrink: 0 };
const rateName    = { flex: 1, fontSize: 13, color: "#111827" };
const rateAmt     = { fontSize: 13, fontWeight: 800, color: PURPLE };

// markets section
const section     = { padding: "32px 24px", maxWidth: 1200, margin: "0 auto" };
const secHead     = { display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" };
const secTitle    = { margin: 0, fontSize: "clamp(20px,3vw,26px)", fontWeight: 900, color: PURPLE };
const secSub      = { margin: "4px 0 0", color: "#6b7280", fontSize: 14 };
const refreshBtnStyle = { height: 36, padding: "0 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" };
const mktGrid     = { display: "grid", gap: 14 };
const mktCard     = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 18px", boxShadow: "0 2px 8px rgba(0,0,0,.04)" };
const mktTopRow   = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 };
const mktName     = { fontSize: 16, fontWeight: 900, color: "#111827", textTransform: "uppercase", letterSpacing: "0.04em" };
const statusPill  = { fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999 };
const mktTime     = { fontSize: 12, color: "#6b7280", marginBottom: 6 };
const mktResult   = { fontSize: 22, fontWeight: 900, color: "#111827", letterSpacing: "0.06em", marginBottom: 10 };
const mktFoot     = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" };
const chartRow    = { display: "inline-flex", alignItems: "center", gap: 5 };
const chartLnk    = { border: 0, background: "transparent", padding: 0, color: ORANGE, fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" };
const divider     = { color: "#d1d5db", fontSize: 12 };
const playNowBtn  = { height: 38, padding: "0 16px", borderRadius: 8, border: 0, background: PURPLE, color: "#fff", fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", whiteSpace: "nowrap" };

// download strip
const dlStrip     = { background: GREEN, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 14, padding: "18px 24px", flexWrap: "wrap" };
const dlBtn       = { height: 40, padding: "0 20px", borderRadius: 8, border: "2px solid #fff", background: "transparent", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };

// ─── SEO Section Data ────────────────────────────────────────────────────────
const HOME_FEATURES = [
  { icon: "💰", title: "Full Game Rates Guaranteed", desc: "Single 9×, Jodi 90×, Single Patti 150×, Double Patti 270×, Triple Patti 800×. Best rates in India — guaranteed every session." },
  { icon: "⚡", title: "Instant Deposit & Withdrawal", desc: "UPI, PhonePe, Google Pay — deposits credited in seconds. Withdrawals processed daily. Fast, secure and verified every time." },
  { icon: "📊", title: "Live Market Results", desc: "Kalyan, Milan, Rajdhani, Main Bazar and Starline results updated live. Full Jodi and Panel chart history available anytime." },
  { icon: "🔒", title: "Secure OTP Login", desc: "No password needed. Your account is protected by mobile OTP every time. Your wallet and bids are always safe with us." },
  { icon: "🎮", title: "8 Game Types", desc: "Single Digit, Jodi, Red Bracket, Single Patti, Double Patti, Triple Patti, Half Sangam and Full Sangam — all in one platform." },
  { icon: "📱", title: "Works on All Devices", desc: "Play on Android, iPhone, tablet or desktop browser. Download the free app or play directly on the website — your choice." },
];

const HOW_STEPS = [
  { title: "Register Free", desc: "Enter your mobile number, verify with OTP and create your account in under 30 seconds. No documents needed." },
  { title: "Add Money to Wallet", desc: "Deposit using UPI, PhonePe or Google Pay. Minimum deposit is low and credits are instant after verification." },
  { title: "Choose Your Market", desc: "Pick from Kalyan, Milan, Rajdhani, Main Bazar or Starline markets. Check open/close times and current status." },
  { title: "Place Your Bid & Win", desc: "Choose game type (Single, Jodi, Patti), enter your number and amount. Win up to 800× your bet when results are declared!" },
];

const HOME_REVIEWS = [
  { name: "Rajesh Bhatia", color: "#7c3aed", stars: 5, text: "Best matka platform I have used. Withdrawal done in same day. Full rates given — 150× on single patti. Very trusted app.", date: "10 Jul, 2026" },
  { name: "Suresh Kumar", color: "#059669", stars: 5, text: "Kalyan and Milan results update instantly. Jodi chart is very clear. Deposit from PhonePe works in seconds. Highly recommended!", date: "9 Jul, 2026" },
  { name: "Priya Sharma", color: "#ea580c", stars: 5, text: "OTP login is very safe. My wallet is always protected. Customer support helped me with withdrawal. 5 stars without doubt.", date: "8 Jul, 2026" },
  { name: "Ankit Verma", color: "#2563eb", stars: 5, text: "Playing here for 6 months. Main Bazar result always on time. App works fast on my Android. Best rates in market — triple patti 800×!", date: "7 Jul, 2026" },
];

const HOME_FAQS = [
  { q: "What is Satta Matka?", a: "Satta Matka is a popular number-based game of chance in India. Players bet on numbers (Single Digit, Jodi, or Patti) and win based on the declared result. Markets like Kalyan, Milan, and Rajdhani operate at specific times daily." },
  { q: "How do I play online Matka on this website?", a: "Register with your mobile number, verify via OTP, add money to your wallet via UPI, choose a market, select a game type (Single, Jodi, Patti etc.), enter your number and bet amount, then wait for results." },
  { q: "What are the game rates on Play Online Satta Matka?", a: "Single Digit wins 9× your bet, Jodi wins 90×, Single Patti wins 150×, Double Patti wins 270×, Triple Patti wins 800×, Half Sangam wins 1500× and Full Sangam wins 11250×. These are full market rates." },
  { q: "How do I deposit money?", a: "Go to Wallet → Add Money. Enter the amount and scan the UPI QR code or use the UPI ID shown. Pay via PhonePe, Google Pay, Paytm or any UPI app. Upload the payment screenshot and your balance is credited after verification." },
  { q: "How long does withdrawal take?", a: "Withdrawal requests are processed within the same day during business hours. Add your bank account or UPI details in your profile first. Minimum withdrawal amount is shown on the withdrawal page." },
  { q: "What is a Jodi in Satta Matka?", a: "Jodi means a pair of two digits (00 to 99). If you bet on Jodi 45 and the market result shows 45 as the Jodi (formed from open and close digits), you win 90 times your bet amount." },
  { q: "What is the difference between Single Patti and Double Patti?", a: "Single Patti (SP) is a 3-digit number where all digits are different (e.g., 123). Double Patti (DP) has two same digits (e.g., 112). Triple Patti (TP) has all three digits same (e.g., 111). SP wins 150×, DP wins 270×, TP wins 800×." },
  { q: "Is this platform safe to use?", a: "Yes. Play Online Satta Matka uses OTP-based login (no stored passwords), encrypted wallet transactions and verified payment processing. Your personal data is never sold. We follow responsible gaming guidelines — players must be 18+." },
];

// ─── SEO Styles ──────────────────────────────────────────────────────────────
const seoSection  = { padding: "48px 24px", background: "#fff" };
const seoInner    = { maxWidth: 1200, margin: "0 auto" };
const seoCategoryTag = { margin: "0 0 8px", fontSize: 13, fontWeight: 800, color: ORANGE, textTransform: "uppercase", letterSpacing: "0.08em" };
const seoH2       = { margin: "0 0 12px", fontSize: "clamp(22px,3.5vw,32px)", fontWeight: 900, color: "#111827" };
const seoLead     = { margin: "0 0 28px", fontSize: 16, color: "#374151", maxWidth: 700 };
const seoBody     = { margin: "0 0 14px", fontSize: 15, color: "#374151", lineHeight: 1.7 };
const seoTwoCol   = { display: "grid", gridTemplateColumns: "1fr 380px", gap: 32, alignItems: "start", flexWrap: "wrap" };
const seoCtaRow   = { marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" };
const seoPrimaryBtn  = { height: 46, padding: "0 24px", borderRadius: 10, border: 0, background: ORANGE, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" };
const seoSecondaryBtn = { height: 46, padding: "0 22px", borderRadius: 10, border: `2px solid ${PURPLE}`, background: "#fff", color: PURPLE, fontWeight: 800, fontSize: 15, cursor: "pointer" };

// features
const featGrid    = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, marginTop: 8 };
const featCard    = { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: "22px 18px" };
const featIcon    = { fontSize: 28, marginBottom: 10 };
const featTitle   = { margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: "#111827" };
const featBody    = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 };

// rate info box
const rateInfoBox   = { border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.07)" };
const rateInfoHead  = { background: PURPLE, color: "#fff", padding: "12px 16px", fontWeight: 800, fontSize: 14 };
const rateInfoRow   = { display: "flex", justifyContent: "space-between", padding: "9px 16px", borderBottom: "1px solid #f3f4f6", fontSize: 13 };
const rateInfoName  = { color: "#374151" };
const rateInfoVal   = { fontWeight: 800, color: ORANGE };

// steps
const stepsGrid   = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginTop: 8 };
const stepCard    = { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: "20px 16px", position: "relative" };
const stepNum     = { width: 36, height: 36, borderRadius: "50%", background: PURPLE, color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 16, marginBottom: 12 };
const stepTitle   = { margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: "#111827" };
const stepDesc    = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 };

// reviews
const ratingBig   = { display: "flex", alignItems: "center", gap: 20, marginBottom: 28, flexWrap: "wrap" };
const ratingScore = { fontSize: 56, fontWeight: 900, color: "#111827", lineHeight: 1 };
const starsRow    = { fontSize: 24, color: "#f59e0b", letterSpacing: 3 };
const ratingNote  = { margin: "4px 0 0", fontSize: 13, color: "#6b7280" };
const reviewGrid  = { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 };
const reviewCard  = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 18px 14px" };
const reviewTop   = { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 };
const reviewAvatar = { width: 42, height: 42, borderRadius: "50%", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 16, flexShrink: 0 };
const reviewName  = { fontSize: 14, fontWeight: 800, color: "#111827" };
const reviewStars = { fontSize: 13, color: "#f59e0b", letterSpacing: 2 };
const verifiedBadge = { marginLeft: "auto", background: "#dcfce7", color: "#15803d", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, flexShrink: 0 };
const reviewText  = { margin: "0 0 10px", fontSize: 13, color: "#374151", lineHeight: 1.6 };
const reviewDate  = { fontSize: 11, color: "#9ca3af" };

// faq
const faqList     = { display: "grid", gap: 10, marginTop: 8 };
const faqItem     = { border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff" };
const faqQuestion = { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", border: 0, background: "transparent", textAlign: "left", fontWeight: 700, fontSize: 15, color: "#111827", cursor: "pointer", gap: 12 };
const faqChevron  = { fontSize: 20, fontWeight: 900, color: ORANGE, flexShrink: 0 };
const faqAnswer   = { margin: 0, padding: "0 18px 16px", fontSize: 14, color: "#374151", lineHeight: 1.7 };

// footer
const ftr         = { background: "#111827", color: "#fff", padding: "32px 24px", textAlign: "center" };
const ftrNote     = { fontSize: 13, color: "#9ca3af", marginBottom: 16, maxWidth: 600, margin: "8px auto 16px" };
const ftrLinks    = { display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", marginBottom: 18 };
const ftrLnk      = { border: 0, background: "transparent", color: "#d1d5db", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "none" };
const ftrDivider  = { width: "100%", height: 1, background: "rgba(255,255,255,.12)", margin: "4px 0 12px" };
const ftrCopy     = { fontSize: 12, color: "#6b7280", margin: 0 };

export default Home;
